import React from "react";
import { createPortal } from "react-dom";
import QRCode from "react-qr-code";
import { useRemoteControl } from "./RemoteControlContext";
import { t } from "../i18n";

function RemoteIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M10 5.5h4M11 18.5h2" strokeLinecap="round" />
      <path d="M3 9.5a5 5 0 0 1 3-4.6M21 9.5a5 5 0 0 0-3-4.6" strokeLinecap="round" />
    </svg>
  );
}

export default function RemotePairingButton({ mobile = false }) {
  const {
    session, status, controllerConnected, dialogOpen, error,
    setDialogOpen, startPairing, resetPairing, endPairing,
  } = useRemoteControl();

  const open = () => {
    setDialogOpen(true);
    startPairing();
  };
  const expired = session?.pairExpiresAt && Date.now() / 1000 >= session.pairExpiresAt;

  React.useEffect(() => {
    if (!dialogOpen || !controllerConnected) return undefined;
    const timer = window.setTimeout(() => setDialogOpen(false), 3000);
    return () => window.clearTimeout(timer);
  }, [controllerConnected, dialogOpen, setDialogOpen]);

  return (
    <>
      <button
        type="button"
        onClick={open}
        className={`${mobile ? "mt-2 w-full justify-start px-4 py-3" : "h-10 px-3"} flex items-center gap-2 rounded-xl border border-cyan-300/20 bg-cyan-400/[0.07] text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300`}
        aria-label={t("Connect phone remote")}
      >
        <span className="relative"><RemoteIcon />{controllerConnected && <span className="absolute -end-1 -top-1 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-950" />}</span>
        <span>{controllerConnected ? t("Remote connected") : t("Phone remote")}</span>
      </button>

      {!mobile && dialogOpen && createPortal(
        <div
          className="fixed inset-0 z-[2000] flex overflow-y-auto bg-slate-950/80 p-4 backdrop-blur sm:p-6"
          onClick={() => setDialogOpen(false)}
        >
          <section
            className="relative mx-auto my-auto w-full max-w-md rounded-[2rem] border border-white/10 bg-slate-900 p-6 text-center shadow-2xl shadow-black/60"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remote-pair-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" onClick={() => setDialogOpen(false)} className="absolute end-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-white/5 text-indigo-100 hover:bg-white/10" aria-label={t("Close")}>×</button>
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-cyan-400/10 text-cyan-200 ring-1 ring-cyan-300/20"><RemoteIcon /></div>
            <h2 id="remote-pair-title" className="mt-4 text-2xl font-black text-white">{t("Phone remote")}</h2>

            {session?.controllerClaimed ? (
              <div className="mt-6">
                <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full text-4xl ring-1 ${controllerConnected ? "bg-emerald-400/10 text-emerald-300 ring-emerald-300/30" : "bg-amber-300/10 text-amber-200 ring-amber-300/30"}`}>{controllerConnected ? "✓" : "…"}</div>
                <p className={`mt-4 font-bold ${controllerConnected ? "text-emerald-100" : "text-amber-100"}`}>{controllerConnected ? t("A phone is connected") : t("Waiting for the paired phone")}</p>
                <p className="mt-2 text-sm text-indigo-100/60">{t("The QR is hidden and no second controller can join.")}</p>
              </div>
            ) : status === "creating" || status === "connecting" || status === "reconnecting" ? (
              <div className="py-12 text-indigo-100/70">{status === "reconnecting" ? t("Reconnecting…") : t("Preparing secure pairing…")}</div>
            ) : session?.pairUrl && !expired ? (
              <div className="mt-6">
                <div className="mx-auto w-fit rounded-2xl bg-white p-4"><QRCode value={session.pairUrl} size={210} /></div>
                <p className="mt-4 text-sm text-indigo-100/70">{t("Scan with the phone that will control this screen.")}</p>
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-amber-300/20 bg-amber-300/10 p-5 text-amber-100">
                <p>{error || t("The pairing code expired.")}</p>
                <button type="button" onClick={resetPairing} className="mt-4 rounded-xl bg-amber-200 px-4 py-2 font-bold text-slate-950">{t("Generate new QR")}</button>
              </div>
            )}

            {error && session?.pairUrl && !expired ? <p className="mt-4 text-sm text-rose-200">{error}</p> : null}
            {session ? (
              <div className="mt-6 flex flex-wrap justify-center gap-3 border-t border-white/10 pt-5">
                <button type="button" onClick={resetPairing} className="rounded-xl border border-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/5">{controllerConnected ? t("Pair another phone") : t("New QR")}</button>
                <button type="button" onClick={endPairing} className="rounded-xl border border-rose-300/20 bg-rose-400/10 px-4 py-2 text-sm font-bold text-rose-100 hover:bg-rose-400/20">{t("End remote")}</button>
              </div>
            ) : null}
          </section>
        </div>,
        document.body
      )}
    </>
  );
}
